import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import * as Icons from "lucide-react";

const BG_PORTRAIT = "/signa-bg-portrait.webp?v=1";
const BG_LANDSCAPE = "/signa-bg-landscape.webp?v=1";

const MODULES = [
  { icon: "ClipboardCheck", label: "CHECK" },
  { icon: "Route", label: "TRACK" },
  { icon: "FileText", label: "REPORT" },
  { icon: "Wrench", label: "CARE" },
];

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(apiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="relative min-h-[100svh] w-full overflow-hidden bg-[#0D2E24]" data-testid="team-login-page">
      <img src={BG_PORTRAIT} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover object-center lg:hidden" loading="eager" />
      <img src={BG_LANDSCAPE} alt="" aria-hidden="true" className="absolute inset-0 hidden h-full w-full object-cover object-center lg:block" loading="eager" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0D2E24]/75 via-[#0D2E24]/35 to-transparent" aria-hidden="true" />

      <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-7xl flex-col px-5 py-8 lg:flex-row lg:items-stretch lg:gap-12 lg:px-10">
        {/* Colonne gauche : marque, accroche, modules */}
        <div className="flex flex-1 flex-col lg:py-12">
          <div className="text-center lg:text-left">
            <span className="signa-wordmark text-3xl drop-shadow-lg sm:text-4xl">
              <span className="text-[#A3E635]">SIGNA</span> <span className="text-white">SYSTEM</span>
            </span>
          </div>
          <div className="hidden flex-1 flex-col justify-center lg:flex">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#A3E635]">Espace équipe</p>
            <div className="mt-3 h-[3px] w-14 bg-[#A3E635]" />
            <h1 className="mt-5 font-display text-4xl font-bold leading-[1.05] text-white drop-shadow-xl sm:text-5xl lg:text-6xl">
              Vos opérations,<br />au même endroit.
            </h1>
            <p className="mt-5 max-w-md text-base text-white/90 drop-shadow">
              Préparez vos visites, organisez vos interventions et assurez le suivi de vos clients.
            </p>
          </div>
          <div className="mt-6 hidden flex-wrap items-center gap-x-5 gap-y-2 lg:flex">
            {MODULES.map((m) => {
              const I = Icons[m.icon];
              return (
                <span key={m.label} className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-white/90 drop-shadow">
                  <I className="h-4 w-4 text-[#A3E635]" /> {m.label}
                </span>
              );
            })}
          </div>
          <p className="mt-5 hidden text-[11px] font-medium uppercase tracking-wider text-white/70 lg:block">
            Sécuriser, valoriser, innover
          </p>
        </div>

        {/* Colonne droite : carte de connexion */}
        <div className="flex items-center justify-center lg:w-[440px]">
          <form onSubmit={submit} data-testid="team-login-form"
            className="w-full max-w-[460px] rounded-2xl border border-white/50 bg-white/85 p-6 shadow-2xl backdrop-blur-[18px] [-webkit-backdrop-filter:blur(18px)] supports-[backdrop-filter]:bg-white/70 sm:p-8">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-700">Accès professionnel</p>
            <h2 className="mt-1 font-display text-2xl font-bold text-[#0D2E24] sm:text-3xl">Connexion équipe</h2>
            <p className="mt-1.5 text-sm text-slate-600">Accédez à votre espace de travail SIGNA SYSTEM.</p>

            {error && (
              <div className="mt-4 flex items-start gap-2 rounded-lg border border-rose-300 bg-rose-50/90 p-3 text-sm text-rose-700" data-testid="team-login-error" role="alert">
                <Icons.AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
              </div>
            )}

            <div className="mt-5 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="team-email" className="text-[#0D2E24]">Adresse e-mail professionnelle</Label>
                <Input id="team-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  className="h-12 bg-white/80 text-base" data-testid="team-login-email" placeholder="prenom@signaclean.fr" autoComplete="username" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="team-password" className="text-[#0D2E24]">Mot de passe</Label>
                <div className="relative">
                  <Input id="team-password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required
                    className="h-12 bg-white/80 pr-12 text-base" data-testid="team-login-password" placeholder="••••••••" autoComplete="current-password" />
                  <button type="button" onClick={() => setShowPw((s) => !s)}
                    aria-label={showPw ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    className="absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-md text-slate-500 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600"
                    data-testid="team-login-toggle-password">
                    {showPw ? <Icons.EyeOff className="h-5 w-5" /> : <Icons.Eye className="h-5 w-5" />}
                  </button>
                </div>
                <div className="text-right">
                  <button type="button" onClick={() => setForgot(true)} className="text-sm font-medium text-emerald-700 hover:underline" data-testid="team-login-forgot">
                    Mot de passe oublié ?
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading} data-testid="team-login-submit"
                className="h-12 w-full bg-emerald-700 text-base font-semibold hover:bg-emerald-800">
                {loading ? <Icons.Loader2 className="h-5 w-5 animate-spin" />
                  : <span className="flex items-center justify-center gap-2">Accéder à mon espace de travail <Icons.ArrowRight className="h-5 w-5" /></span>}
              </Button>
            </div>

            <div className="mt-5 border-t border-slate-200 pt-4">
              <p className="text-sm text-slate-500">Accès réservé aux collaborateurs autorisés.</p>
              <a href="https://www.signaclean.fr" className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:underline" data-testid="team-login-back-site">
                <Icons.ArrowLeft className="h-4 w-4" /> Retour au site SIGNA CLEAN
              </a>
            </div>
          </form>
        </div>
      </div>

      <Dialog open={forgot} onOpenChange={setForgot}>
        <DialogContent data-testid="team-forgot-dialog">
          <DialogHeader>
            <DialogTitle>Mot de passe oublié</DialogTitle>
            <DialogDescription>
              Pour des raisons de sécurité, la réinitialisation est gérée par votre administrateur SIGNA SYSTEM.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2.5 text-sm text-slate-600">
            <p>Contactez votre administrateur pour réinitialiser votre mot de passe :</p>
            <a href="mailto:contact@signaclean.com" className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 font-medium text-emerald-700 hover:bg-slate-100" data-testid="team-forgot-email">
              <Icons.Mail className="h-4 w-4" /> contact@signaclean.com
            </a>
            <a href="tel:+596696687766" className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 font-medium text-emerald-700 hover:bg-slate-100" data-testid="team-forgot-phone">
              <Icons.Phone className="h-4 w-4" /> +596 696 68 77 66
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
